import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Team, Player } from '../types';
import { mediaUrl } from '../utils/mediaUrl';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

interface HistoryPlayer {
  player_id:    string;
  player_name:  string;
  player_photo: string | null;
  ack_count:    number;
  reply_count:  number;
}

interface PostHistoryItem {
  bulk_id:         string;
  title:           string;
  content:         string;
  video_url:       string | null;
  author_username: string;
  created_at:      string;
  players:         HistoryPlayer[];
}

// ── Small helper to format relative dates ─────────────────────────────────────
function relativeDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ── Post History Card ─────────────────────────────────────────────────────────
const HistoryCard = ({ item }: { item: PostHistoryItem }) => {
  const [expanded, setExpanded] = useState(false);

  const ackedCount  = item.players.filter(p => p.ack_count > 0).length;
  const totalCount  = item.players.length;
  const allAcked    = ackedCount === totalCount && totalCount > 0;
  const ackPct      = totalCount > 0 ? Math.round((ackedCount / totalCount) * 100) : 0;

  return (
    <div style={{
      background: '#141820', border: '1px solid #252B38', borderRadius: '12px', overflow: 'hidden',
    }}>
      {/* Card header */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '14px' }}
      >
        {/* Ack progress ring (simple pill) */}
        <div style={{
          flexShrink: 0, marginTop: '2px',
          width: 44, height: 44, borderRadius: '50%',
          background: allAcked ? 'rgba(34,197,94,0.12)' : 'rgba(249,115,22,0.08)',
          border: `2px solid ${allAcked ? '#22C55E' : '#F97316'}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1,
        }}>
          <div style={{ fontSize: '13px', fontWeight: 900, color: allAcked ? '#22C55E' : '#F97316' }}>
            {ackedCount}
          </div>
          <div style={{ fontSize: '8px', color: '#4A5568', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            /{totalCount}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: '14px', marginBottom: '3px', color: '#F0F6FC' }}>
            {item.title}
          </div>
          <div style={{ fontSize: '12px', color: '#8892A4', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <span>{relativeDate(item.created_at)}</span>
            <span style={{ color: '#252B38' }}>·</span>
            <span>
              {totalCount} player{totalCount !== 1 ? 's' : ''}
            </span>
            <span style={{ color: '#252B38' }}>·</span>
            <span style={{ color: allAcked ? '#22C55E' : ackedCount > 0 ? '#F97316' : '#4A5568', fontWeight: 700 }}>
              {ackedCount === 0 ? 'No acknowledgements yet' : `${ackedCount}/${totalCount} acknowledged (${ackPct}%)`}
            </span>
          </div>

          {/* Progress bar */}
          {totalCount > 0 && (
            <div style={{ marginTop: '8px', height: '3px', background: '#252B38', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${ackPct}%`,
                background: allAcked ? '#22C55E' : '#F97316',
                borderRadius: '2px', transition: 'width 0.4s',
              }} />
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0, color: '#4A5568', fontSize: '14px', marginTop: '2px' }}>
          {expanded ? '▲' : '▼'}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: '1px solid #1A2030', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Message preview */}
          <div style={{
            padding: '12px 14px', background: '#0F1117', borderRadius: '8px',
            fontSize: '13px', color: '#8892A4', lineHeight: 1.6,
            borderLeft: '3px solid #252B38',
          }}>
            {item.content}
          </div>

          {/* Video indicator */}
          {item.video_url && (
            <div style={{ fontSize: '12px', color: '#22D3EE', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>🎬</span> Video clip included
            </div>
          )}

          {/* Player ack grid */}
          <div>
            <div style={{
              fontWeight: 700, fontSize: '11px', textTransform: 'uppercase',
              letterSpacing: '0.8px', color: '#4A5568', marginBottom: '10px',
            }}>
              Acknowledgement Status
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {item.players.map(p => {
                const acked = p.ack_count > 0;
                const photoUrl = p.player_photo ? mediaUrl(p.player_photo) : null;
                return (
                  <div key={p.player_id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 12px', borderRadius: '8px',
                    background: acked ? 'rgba(34,197,94,0.06)' : '#0F1117',
                    border: `1px solid ${acked ? 'rgba(34,197,94,0.2)' : '#1A2030'}`,
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                      background: '#252B38', overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 800, color: '#8892A4',
                    }}>
                      {photoUrl
                        ? <img src={photoUrl} alt={p.player_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : p.player_name.charAt(0).toUpperCase()}
                    </div>

                    {/* Name */}
                    <div style={{ flex: 1, fontWeight: 600, fontSize: '13px', color: acked ? '#F0F6FC' : '#8892A4' }}>
                      {p.player_name}
                    </div>

                    {/* Reply count */}
                    {p.reply_count > 0 && (
                      <div style={{
                        fontSize: '11px', color: '#8892A4', display: 'flex', alignItems: 'center', gap: '4px',
                      }}>
                        💬 {p.reply_count}
                      </div>
                    )}

                    {/* Ack badge */}
                    <div style={{
                      flexShrink: 0,
                      width: 22, height: 22, borderRadius: '50%',
                      background: acked ? 'rgba(34,197,94,0.15)' : '#1A2030',
                      border: `1px solid ${acked ? '#22C55E' : '#252B38'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px',
                    }}>
                      {acked ? <span style={{ color: '#22C55E' }}>✓</span> : <span style={{ color: '#252B38' }}>○</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const CoachBulkPost = () => {
  const { isAdmin, isCoach } = useAuth();
  const navigate = useNavigate();

  const [teams, setTeams]                         = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId]       = useState('');
  const [players, setPlayers]                     = useState<Player[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [loadingPlayers, setLoadingPlayers]       = useState(false);

  const [title, setTitle]     = useState('');
  const [content, setContent] = useState('');
  const [clip, setClip]       = useState<File | null>(null);

  const [posting, setPosting]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess]   = useState<number | null>(null);
  const [error, setError]       = useState('');

  const [postHistory, setPostHistory]       = useState<PostHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Redirect non-coaches/non-admins
  useEffect(() => {
    if (!isAdmin && !isCoach) navigate('/teams');
  }, [isAdmin, isCoach, navigate]);

  // Fetch post history
  const fetchHistory = useCallback(() => {
    setHistoryLoading(true);
    api.get('/coach-portal/my-posts')
      .then(r => setPostHistory(r.data))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, []);

  // Fetch teams on mount
  useEffect(() => {
    api.get('/coach-portal/my-teams').then(r => {
      setTeams(r.data);
      if (r.data.length === 1) setSelectedTeamId(r.data[0].id);
    }).catch(() => {});
    fetchHistory();
  }, [fetchHistory]);

  // Fetch players when team changes, auto-select all
  useEffect(() => {
    if (!selectedTeamId) { setPlayers([]); setSelectedPlayerIds([]); return; }
    setLoadingPlayers(true);
    api.get(`/players?team_id=${selectedTeamId}`)
      .then(r => {
        setPlayers(r.data);
        setSelectedPlayerIds(r.data.map((p: Player) => p.id));
      })
      .catch(() => {})
      .finally(() => setLoadingPlayers(false));
  }, [selectedTeamId]);

  const togglePlayer = useCallback((id: string) => {
    setSelectedPlayerIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    setSuccess(null);
  }, []);

  const allSelected  = players.length > 0 && selectedPlayerIds.length === players.length;
  const noneSelected = selectedPlayerIds.length === 0;

  const toggleAll = () => {
    setSelectedPlayerIds(allSelected ? [] : players.map(p => p.id));
    setSuccess(null);
  };

  const handleSubmit = async () => {
    if (!title.trim())   { setError('Title is required'); return; }
    if (!content.trim()) { setError('Message is required'); return; }
    if (noneSelected)    { setError('Select at least one player'); return; }

    setPosting(true); setError(''); setSuccess(null);

    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('content', content.trim());
    selectedPlayerIds.forEach(id => fd.append('playerIds', id));
    if (clip) fd.append('clip', clip);

    try {
      const r = await api.post('/coach-portal/bulk', fd, {
        onUploadProgress: (e: any) => {
          if (clip && e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        timeout: 600_000,
      });
      setSuccess(r.data.count);
      setTitle('');
      setContent('');
      setClip(null);
      setProgress(0);
      const fileInput = document.getElementById('clip-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      setSelectedPlayerIds(players.map(p => p.id));
      // Refresh history to show the new post
      fetchHistory();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to post. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const selectedTeam = teams.find(t => t.id === selectedTeamId);

  return (
    <div className="container" style={{ maxWidth: '860px' }}>

      {/* Page header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 900, letterSpacing: '-0.5px', marginBottom: '4px' }}>
          📢 Coach Portal
        </h1>
        <p style={{ color: '#8892A4', fontSize: '14px' }}>
          Post coaching feedback to your entire team, a group of players, or an individual.
        </p>
      </div>

      {/* No teams state */}
      {teams.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', background: '#141820', border: '1px solid #252B38', borderRadius: '16px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏆</div>
          <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>No teams assigned</h3>
          <p style={{ color: '#8892A4', fontSize: '14px' }}>
            Ask an admin to assign you to a team in the Admin panel → Teams &amp; Players → 👥 Coaches.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ── Team picker ── */}
          {teams.length > 1 && (
            <div style={{ background: '#141820', border: '1px solid #252B38', borderRadius: '12px', padding: '16px 20px' }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#8892A4', marginBottom: '10px' }}>
                Team
              </label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {teams.map(t => (
                  <button
                    key={t.id}
                    onClick={() => { setSelectedTeamId(t.id); setSuccess(null); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
                      border: selectedTeamId === t.id ? `2px solid ${t.primary_color}` : '2px solid #252B38',
                      background: selectedTeamId === t.id ? `${t.primary_color}18` : '#0F1117',
                      color: selectedTeamId === t.id ? '#F0F6FC' : '#8892A4',
                      fontWeight: selectedTeamId === t.id ? 700 : 500,
                      fontSize: '14px', transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: t.primary_color, flexShrink: 0 }} />
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Player selection grid ── */}
          {selectedTeamId && (
            <div style={{ background: '#141820', border: '1px solid #252B38', borderRadius: '12px', padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#8892A4' }}>
                  Players
                  {selectedPlayerIds.length > 0 && (
                    <span style={{ color: '#F97316', marginLeft: '8px', fontSize: '12px' }}>
                      {selectedPlayerIds.length} / {players.length} selected
                    </span>
                  )}
                </div>
                {!loadingPlayers && players.length > 0 && (
                  <button
                    onClick={toggleAll}
                    style={{
                      background: 'none',
                      border: `1px solid ${allSelected ? '#F97316' : '#252B38'}`,
                      color: allSelected ? '#F97316' : '#8892A4',
                      borderRadius: '8px', padding: '5px 14px',
                      fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {allSelected ? '✓ All Selected' : 'Select All'}
                  </button>
                )}
              </div>

              {loadingPlayers ? (
                <div style={{ color: '#4A5568', fontSize: '13px', textAlign: 'center', padding: '24px' }}>Loading players…</div>
              ) : players.length === 0 ? (
                <div style={{ color: '#4A5568', fontSize: '13px' }}>No players on this team yet.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                  {players.map(player => {
                    const sel = selectedPlayerIds.includes(player.id);
                    const photoUrl = player.photo_url ? mediaUrl(player.photo_url) : null;
                    return (
                      <div
                        key={player.id}
                        onClick={() => togglePlayer(player.id)}
                        style={{
                          padding: '12px 8px', borderRadius: '12px', cursor: 'pointer',
                          border: sel ? '2px solid #F97316' : '2px solid #252B38',
                          background: sel ? 'rgba(249,115,22,0.08)' : '#0F1117',
                          textAlign: 'center', transition: 'all 0.15s', position: 'relative',
                          userSelect: 'none',
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: '7px', right: '7px',
                          width: '18px', height: '18px', borderRadius: '50%',
                          background: sel ? '#F97316' : '#252B38',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '9px', fontWeight: 900, color: '#fff',
                          transition: 'background 0.15s',
                        }}>
                          {sel ? '✓' : ''}
                        </div>
                        <div style={{
                          width: 48, height: 48, borderRadius: '50%',
                          background: selectedTeam?.primary_color || '#F97316',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: 900, color: '#fff',
                          margin: '0 auto 8px', overflow: 'hidden', flexShrink: 0,
                        }}>
                          {photoUrl
                            ? <img src={photoUrl} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : `#${player.number ?? '?'}`}
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: sel ? '#F0F6FC' : '#8892A4', lineHeight: 1.3 }}>
                          {player.name}
                        </div>
                        {player.position && (
                          <div style={{ fontSize: '10px', color: '#4A5568', marginTop: '2px' }}>{player.position}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Post form ── */}
          {selectedTeamId && !loadingPlayers && players.length > 0 && (
            <div style={{ background: '#141820', border: '1px solid #252B38', borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#8892A4' }}>
                Post Content
              </div>

              {success !== null && (
                <div style={{
                  padding: '14px 16px', background: 'rgba(34,197,94,0.1)',
                  border: '1px solid rgba(34,197,94,0.25)', borderRadius: '10px',
                  color: '#22C55E', fontWeight: 700, fontSize: '14px',
                }}>
                  ✅ Posted to {success} player{success !== 1 ? 's' : ''} successfully!
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Title</label>
                <input
                  className="form-control"
                  value={title}
                  onChange={e => { setTitle(e.target.value); setSuccess(null); }}
                  placeholder="e.g. Great hustle today — a few things to work on"
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Message</label>
                <textarea
                  className="form-control"
                  value={content}
                  onChange={e => { setContent(e.target.value); setSuccess(null); }}
                  placeholder="Write your coaching feedback here…"
                  rows={6}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Video Clip (optional)</label>
                <input
                  id="clip-input"
                  type="file"
                  accept="video/*"
                  className="form-control"
                  style={{ padding: '8px' }}
                  onChange={e => { setClip(e.target.files?.[0] || null); setSuccess(null); }}
                />
                {clip && (
                  <div style={{ fontSize: '12px', color: '#8892A4', marginTop: '4px' }}>
                    {clip.name} · {(clip.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
              </div>

              {clip && posting && progress > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#8892A4', marginBottom: '6px' }}>
                    <span>Uploading clip…</span><span>{progress}%</span>
                  </div>
                  <div style={{ height: '5px', background: '#252B38', borderRadius: '3px' }}>
                    <div style={{ height: '100%', width: `${progress}%`, background: '#F97316', borderRadius: '3px', transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              {error && <div style={{ color: '#F87171', fontSize: '13px' }}>{error}</div>}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '13px', color: noneSelected ? '#F87171' : '#8892A4' }}>
                  {noneSelected
                    ? '⚠ No players selected'
                    : `Will post to ${selectedPlayerIds.length} player${selectedPlayerIds.length !== 1 ? 's' : ''}`}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={posting || noneSelected || !title.trim() || !content.trim()}
                  style={{ minWidth: '200px' }}
                >
                  {posting
                    ? (clip && progress > 0 ? `Uploading… ${progress}%` : 'Posting…')
                    : `📢 Post to ${selectedPlayerIds.length} Player${selectedPlayerIds.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Post History ─────────────────────────────────────────────────── */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '2px' }}>Prior Posts</h2>
            <p style={{ fontSize: '13px', color: '#8892A4' }}>
              Track which players have acknowledged your feedback.
            </p>
          </div>
          <button
            onClick={fetchHistory}
            disabled={historyLoading}
            className="btn btn-ghost btn-sm"
          >
            {historyLoading ? '⟳' : '↻ Refresh'}
          </button>
        </div>

        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#4A5568', fontSize: '14px' }}>
            Loading history…
          </div>
        ) : postHistory.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            background: '#141820', border: '1px solid #252B38', borderRadius: '16px',
          }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>📭</div>
            <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '6px' }}>No posts yet</div>
            <div style={{ fontSize: '13px', color: '#8892A4' }}>
              Once you post feedback above it will appear here with acknowledgement tracking.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {postHistory.map(item => (
              <HistoryCard key={item.bulk_id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachBulkPost;
